import React, { useState, useEffect, useRef, useCallback } from 'react';
import { calculatePoints } from '../utils/scoring';

interface AnimatedSliderProps {
  actualYear?: number;
  minYear?: number;
  maxYear?: number;
  cycleTime?: number;
}

const AnimatedSlider: React.FC<AnimatedSliderProps> = ({
  actualYear = 1967,
  minYear = 1942,
  maxYear = 1967,
  cycleTime = 6000,
}) => {
  // Use refs to store animation data
  const animationRef = useRef<number>(0);
  const startTimeRef = useRef<number>(0);

  // State for current position and score
  const [guessYear, setGuessYear] = useState(minYear);
  const [guessPosition, setGuessPosition] = useState(0.35);
  const [score, setScore] = useState(0);

  // Fixed position for actual year
  const actualYearPosition = 0.65;

  // Animation function
  const animate = useCallback(
    (timestamp: number) => {
      if (!startTimeRef.current) {
        startTimeRef.current = timestamp;
      }

      // Calculate elapsed time since animation started
      const elapsed = timestamp - startTimeRef.current;

      // Calculate the current phase in the animation cycle (0 to 1)
      const phase = (elapsed % cycleTime) / cycleTime;

      // Create a smoother animation curve with easing
      // First half: guess moves toward actual (easeInOutQuad)
      // Second half: reset quickly but smoothly (easeInOutCubic)
      let progress: number;

      if (phase < 0.8) {
        // Moving toward the actual year (0 to 1)
        const adjustedPhase = phase / 0.8;

        // easeInOutQuad
        progress =
          adjustedPhase < 0.5
            ? 2 * adjustedPhase * adjustedPhase
            : 1 - Math.pow(-2 * adjustedPhase + 2, 2) / 2;
      } else {
        // Resetting back to starting position (1 to 0)
        const adjustedPhase = (phase - 0.8) / 0.2;

        // easeInOutCubic for reset
        progress =
          1 -
          (adjustedPhase < 0.5
            ? 4 * Math.pow(adjustedPhase, 3)
            : 1 - Math.pow(-2 * adjustedPhase + 2, 3) / 2);
      }

      // Calculate the current guess position and year based on progress
      const yearDifference = maxYear - minYear;
      const currentYear = Math.round(minYear + progress * yearDifference);
      const positionDifference = actualYearPosition - 0.35;
      const currentPosition = 0.35 + progress * positionDifference;

      // Calculate current score
      const currentScore = calculatePoints(currentYear, actualYear);

      // Update all states together
      setGuessYear(currentYear);
      setGuessPosition(currentPosition);
      setScore(currentScore);

      // Continue animation
      animationRef.current = requestAnimationFrame(animate);
    },
    [cycleTime, minYear, maxYear, actualYearPosition, actualYear]
  );

  // Start animation on component mount
  useEffect(() => {
    animationRef.current = requestAnimationFrame(animate);

    // Clean up animation on unmount
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current);
      }
    };
  }, [animate]);

  return (
    <div className="w-full px-4 py-3 bg-[#1A1321] rounded-lg flex items-center flex-col">
      <div className="relative h-12 w-full">
        {/* Slider Track */}
        <div
          className="absolute inset-0 bg-gray-700 rounded-full"
          style={{
            height: '4px',
            top: '50%',
            transform: 'translateY(-50%)',
          }}
        />

        {/* User Guess Marker */}
        <div
          className="absolute w-3 h-3 rounded-full bg-white"
          style={{
            left: `calc(${guessPosition * 100}% - 1.5px)`,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
            transition: 'none', // Remove transition for smooth animation
          }}
        />

        {/* Actual Year Marker */}
        <div
          className="absolute w-3 h-3 rounded-full bg-[#C6A4FF]"
          style={{
            left: `calc(${actualYearPosition * 100}% - 1.5px)`,
            top: '50%',
            transform: 'translateY(-50%)',
            zIndex: 10,
          }}
        />

        {/* Difference Line */}
        <div
          className="absolute bg-[#C6A4FF]"
          style={{
            height: '2px',
            top: '50%',
            transform: 'translateY(-50%)',
            left: `calc(${guessPosition * 100}% + 1.5px)`,
            width: `calc(${(actualYearPosition - guessPosition) * 100}% - 3px)`,
            zIndex: 2,
            transition: 'none', // Remove transition for smooth animation
          }}
        />

        {/* Guess Year Label */}
        <div
          className="absolute text-white text-xs font-bold"
          style={{
            left: `calc(${guessPosition * 100}% - 10px)`,
            top: '0%',
            marginTop: '-2px',
            textAlign: 'center',
            transition: 'none', // Remove transition for smooth animation
          }}
        >
          {guessYear}
        </div>

        {/* Actual Year Label */}
        <div
          className="absolute text-[#C6A4FF] text-xs font-bold"
          style={{
            left: `calc(${actualYearPosition * 100}% - 10px)`,
            top: '100%',
            marginTop: '-13px',
          }}
        >
          {actualYear}
        </div>
      </div>
      <div className="flex gap-1 justify-self-center mt-4 w-fit">
        <div className="text-[#FFC300] justify-self-center text-[12px] font-bold uppercase">
          +{score}
        </div>
        <div className="text-[#FFC300] justify-self-center text-[12px] font-bold uppercase">
          {' '}
          Pts
        </div>
      </div>
    </div>
  );
};

export default AnimatedSlider;
